// Which public adoption site a visitor landed on (epic-8) — pure, React-free, DOM-free.
//
// One artifact serves both sites. The platform does not redirect or rewrite: it hands out the same
// index.html for every host and every unknown path (SpaResourceConfig), so the SPA itself has to
// decide what to render. That decision has to be synchronous — waiting on a round trip before the
// first paint would flash the wrong site — which is why it reads the address bar rather than config.
//
// Both public brands have two entrances:
//   1. HOSTNAME — the real domain (`ikzoekbaas.nl` or `diermatch.nl`).
//   2. PATH — the developer/acceptance entrance (`/ikzoekbaas` or `/dieren`).
//
// Host wins over path: on ikzoekbaas.nl the visitor is on that site whatever the path says.

export type PublicSite = "IKZOEKBAAS" | "DIERMATCH";

/** The path prefix that opens the ikzoekbaas site on any host (dev/acceptance entrance). */
export const IKZOEKBAAS_PATH_PREFIX = "/ikzoekbaas";

/**
 * True when this hostname is an ikzoekbaas host. Substring, not equality: it must cover
 * `ikzoekbaas.nl`, `www.ikzoekbaas.nl`, `acc.ikzoekbaas.nl` and
 * `ikzoekbaas.dierenbescherming.nl` without a list that goes stale on the first new environment.
 * A port suffix (`ikzoekbaas.local:5173`) is harmless — `location.hostname` never carries one, but
 * a caller passing `location.host` should not break either.
 */
export function isIkZoekBaasHost(hostname: string | null | undefined): boolean {
  return !!hostname && hostname.toLowerCase().includes("ikzoekbaas");
}

/** True for the DierMatch production domain and its www/acc/local variants. */
export function isDierMatchHost(hostname: string | null | undefined): boolean {
  return !!hostname && hostname.toLowerCase().includes("diermatch");
}

/** True when the path is the ikzoekbaas path entrance (`/ikzoekbaas` or below). */
export function isIkZoekBaasPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  const path = pathname.replace(/\/+$/, "");
  return path === IKZOEKBAAS_PATH_PREFIX || path.startsWith(`${IKZOEKBAAS_PATH_PREFIX}/`);
}

/**
 * The site this visitor is on, or null when it is neither (the authenticated app, the nazorg portal,
 * the login routes — everything the Gate already owns). An own public host wins over a path entrance:
 * a stray `/ikzoekbaas` link on diermatch.nl must never switch brands, and vice versa.
 */
export function resolveSite(
  hostname: string | null | undefined,
  pathname: string | null | undefined,
): PublicSite | null {
  if (isIkZoekBaasHost(hostname)) {
    return "IKZOEKBAAS";
  }
  if (isDierMatchHost(hostname)) {
    return "DIERMATCH";
  }
  if (isIkZoekBaasPath(pathname)) {
    return "IKZOEKBAAS";
  }
  if (pathname) {
    const path = pathname.replace(/\/+$/, "");
    if (path === "/dieren" || path.startsWith("/dieren/")) {
      return "DIERMATCH";
    }
  }
  return null;
}
